// =====================================================================
// ADMIN · USUARIOS — gestión de staff
//
// Solo visible para role='admin'. Otros staff verán "No tienes permiso".
//
// Permite:
//   - Listar todos los staff (activos e inactivos)
//   - Agregar nuevo staff por email + nombre + role (invita por correo)
//   - Activar/desactivar
//   - Cambiar role
//   - Eliminar (no borra auth.users, solo la fila staff)
// =====================================================================

import { useEffect, useState } from 'react';
import { useAuth } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type StaffRow = {
  user_id: string;
  display_name: string;
  email: string | null;
  role: 'admin' | 'sales' | 'ops' | 'finance' | 'marketing';
  active: boolean;
  created_at: string;
};

const ROLES: Array<{ value: StaffRow['role']; label: string; hint: string }> = [
  { value: 'admin',     label: 'Administrador', hint: 'Acceso total + gestiona usuarios' },
  { value: 'sales',     label: 'Ventas',         hint: 'Clientes, cotizaciones, pedidos, WhatsApp' },
  { value: 'ops',       label: 'Operaciones',    hint: 'Inventario, compras, entregas' },
  { value: 'finance',   label: 'Finanzas',       hint: 'Pagos, reportes financieros' },
  { value: 'marketing', label: 'Marketing',      hint: 'Contenido, campañas, nurture' },
];

export function AdminUsuarios() {
  const { staff: meStaff, user } = useAuth();
  const [list, setList] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<StaffRow['role']>('sales');
  const [submitting, setSubmitting] = useState(false);
  const [addOk, setAddOk] = useState<{ message: string; magic_link?: string | null; email?: string } | null>(null);

  // Magic link bypass (para desbloquear usuarios rate-limited)
  const [showMagic, setShowMagic] = useState(false);
  const [mlEmail, setMlEmail] = useState('');
  const [mlSubmitting, setMlSubmitting] = useState(false);
  const [mlOk, setMlOk] = useState<{ magic_link: string; email: string; redirect: string } | null>(null);
  const [mlErr, setMlErr] = useState<string | null>(null);

  const isAdmin = meStaff?.role === 'admin';

  // Helper para extraer el mensaje real del error de una edge function
  // (supabase-js solo da "non-2xx status code" — el cuerpo está en error.context).
  const extractErr = async (error: unknown): Promise<string> => {
    if (!error) return 'Error desconocido';
    const e = error as { message?: string; context?: Response | { body?: ReadableStream } };
    try {
      const ctx = e.context as Response | undefined;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        return body?.error || body?.message || e.message || 'Error desconocido';
      }
    } catch { /* ignore */ }
    return e.message || 'Error desconocido';
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    const { data, error } = await supabase.functions.invoke('manage-staff', {
      body: { action: 'list' },
    });
    setLoading(false);
    if (error) { setErr(await extractErr(error)); return; }
    const r = data as { staff?: StaffRow[]; error?: string };
    if (r.error) { setErr(r.error); return; }
    setList(r.staff ?? []);
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) {
    return (
      <AdminContainer eyebrow="Usuarios" title="Gestión de staff">
        <div style={{
          padding: 32, background: theme.bg, border: `1px solid ${theme.border}`,
          textAlign: 'center', color: theme.textMuted,
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 16, color: theme.brand, fontWeight: 700, marginBottom: 6 }}>
            Acceso restringido
          </div>
          <div style={{ fontSize: 13 }}>
            Solo el administrador puede gestionar usuarios. Tu role actual: <code>{meStaff?.role}</code>.
          </div>
        </div>
      </AdminContainer>
    );
  }

  const addStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    setAddOk(null);
    const { data, error } = await supabase.functions.invoke('manage-staff', {
      body: {
        action: 'add',
        email: email.trim(),
        display_name: name.trim(),
        role,
      },
    });
    setSubmitting(false);
    if (error) { setErr(await extractErr(error)); return; }
    const r = data as { ok?: boolean; created?: boolean; email?: string; error?: string; magic_link?: string | null };
    if (r.error) { setErr(r.error); return; }
    setAddOk({
      message: r.created
        ? `Cuenta creada para ${r.email}. Copia el magic link de abajo y pásaselo por WhatsApp/correo para que entre.`
        : `${r.email} ya tenía cuenta — ahora también es staff. Le puedes pasar el magic link de abajo si necesita entrar.`,
      magic_link: r.magic_link,
      email: r.email,
    });
    setEmail(''); setName(''); setRole('sales');
    await load();
  };

  const toggleActive = async (row: StaffRow) => {
    if (!confirm(`¿${row.active ? 'Desactivar' : 'Activar'} a ${row.display_name}?`)) return;
    const { data, error } = await supabase.functions.invoke('manage-staff', {
      body: { action: 'set_active', user_id: row.user_id, active: !row.active },
    });
    if (error) { setErr(await extractErr(error)); return; }
    const r = data as { ok?: boolean; error?: string };
    if (r.error) { setErr(r.error); return; }
    await load();
  };

  const changeRole = async (row: StaffRow, newRole: StaffRow['role']) => {
    const { data, error } = await supabase.functions.invoke('manage-staff', {
      body: { action: 'set_role', user_id: row.user_id, role: newRole },
    });
    if (error) { setErr(await extractErr(error)); return; }
    const r = data as { ok?: boolean; error?: string };
    if (r.error) { setErr(r.error); return; }
    await load();
  };

  const removeStaff = async (row: StaffRow) => {
    if (!confirm(`¿Eliminar a ${row.display_name} del staff? Su cuenta de Supabase NO se borra — solo pierde acceso al admin.`)) return;
    const { data, error } = await supabase.functions.invoke('manage-staff', {
      body: { action: 'remove', user_id: row.user_id },
    });
    if (error) { setErr(await extractErr(error)); return; }
    const r = data as { ok?: boolean; error?: string };
    if (r.error) { setErr(r.error); return; }
    await load();
  };

  const generateMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMlSubmitting(true);
    setMlErr(null);
    setMlOk(null);
    const { data, error } = await supabase.functions.invoke('manage-staff', {
      body: { action: 'magic_link', email: mlEmail.trim() },
    });
    setMlSubmitting(false);
    if (error) { setMlErr(await extractErr(error)); return; }
    const r = data as { ok?: boolean; magic_link?: string; email?: string; redirect?: string; error?: string };
    if (r.error || !r.magic_link) { setMlErr(r.error ?? 'No se pudo generar el link'); return; }
    setMlOk({ magic_link: r.magic_link, email: r.email ?? mlEmail, redirect: r.redirect ?? '' });
    setMlEmail('');
  };

  return (
    <AdminContainer
      eyebrow="Usuarios"
      title="Gestión de staff"
      action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setShowMagic(s => !s); setMlOk(null); setMlErr(null); }} style={btnSecondary}>
            {showMagic ? 'Cerrar' : '🔗 Generar magic link'}
          </button>
          <button onClick={() => { setShowAdd(s => !s); setAddOk(null); setErr(null); }} style={btnPrimary}>
            {showAdd ? 'Cancelar' : '+ Agregar usuario'}
          </button>
        </div>
      }
    >
      {/* Form generar magic link (bypass admin) */}
      {showMagic && (
        <form onSubmit={generateMagicLink} style={{
          background: theme.bg, border: `1px solid ${theme.border}`, padding: 24, marginBottom: 24,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.brand, marginBottom: 6 }}>
            🔗 Generar magic link para cualquier usuario
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, marginBottom: 14 }}>
            Útil cuando un distribuidor o staff queda bloqueado por el rate limit del email
            (max ~4 magic links/hora con SMTP default). El link generado NO consume rate limit
            porque no se manda email — se devuelve aquí para que lo copies y se lo pases por WhatsApp.
            <br/><br/>
            Si el email no existe, se crea la cuenta automáticamente con email verificado.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{
                fontSize: 11, color: theme.textMuted, marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
              }}>Email</div>
              <input
                type="email"
                style={inputStyle}
                value={mlEmail}
                onChange={e => setMlEmail(e.target.value)}
                placeholder="usuario@ejemplo.com"
                required
              />
            </div>
            <button type="submit" disabled={mlSubmitting} style={btnPrimary}>
              {mlSubmitting ? 'Generando…' : 'Generar link'}
            </button>
          </div>
          {mlErr && <div style={{ marginTop: 12 }}><ErrorBanner>{mlErr}</ErrorBanner></div>}
          {mlOk && (
            <div style={{
              marginTop: 14, padding: 14,
              background: theme.accentSoft, border: `1px solid ${theme.accent}`,
              fontSize: 12, color: theme.brand, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Link generado para {mlOk.email}
              </div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>
                Destino al abrir: <code>{mlOk.redirect}</code> · Vence en 1 hora · Un solo uso
              </div>
              <div style={{
                background: theme.bg, border: `1px solid ${theme.border}`,
                padding: 10, marginBottom: 8,
                fontSize: 11, fontFamily: theme.font.mono,
                wordBreak: 'break-all', color: theme.text,
              }}>{mlOk.magic_link}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => {
                  navigator.clipboard.writeText(mlOk.magic_link);
                  alert('Link copiado al portapapeles');
                }} style={{
                  background: theme.brand, color: theme.textOnBrand, border: 'none',
                  padding: '8px 14px', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
                  fontFamily: theme.font.sans,
                }}>📋 Copiar link</button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `Hola, este es tu acceso a NULED:\n\n${mlOk.magic_link}\n\nEs de un solo uso, vence en 1 hora.`
                  )}`}
                  target="_blank" rel="noreferrer"
                  style={{
                    background: '#25D366', color: '#FFFFFF',
                    padding: '8px 14px', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    textDecoration: 'none', fontFamily: theme.font.sans,
                    display: 'inline-block',
                  }}
                >💬 Enviar por WhatsApp</a>
              </div>
            </div>
          )}
        </form>
      )}

      {/* Form agregar */}
      {showAdd && (
        <form onSubmit={addStaff} style={{
          background: theme.bg, border: `1px solid ${theme.border}`, padding: 24, marginBottom: 24,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.brand, marginBottom: 14 }}>
            Nuevo usuario staff
          </div>
          <div style={{
            display: 'grid', gap: 14,
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            marginBottom: 16,
          }}>
            <Field label="Nombre completo">
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
            </Field>
            <Field label="Email">
              <input type="email" style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} required
                placeholder="persona@nuled.mx" />
            </Field>
            <Field label="Role">
              <select style={selectStyle} value={role} onChange={e => setRole(e.target.value as StaffRow['role'])}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
            {ROLES.find(r => r.value === role)?.hint}
            <br />
            Si el email no tiene cuenta en Supabase aún, recibirá un magic link para activarla y entrar directo al admin.
          </div>
          {err && <ErrorBanner>{err}</ErrorBanner>}
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? 'Enviando…' : 'Crear usuario + enviar invitación'}
          </button>
        </form>
      )}

      {/* Mensaje de éxito */}
      {addOk && (
        <div style={{
          background: theme.accentSoft, border: `1px solid ${theme.accent}`,
          padding: 16, marginBottom: 16, color: theme.brand, fontSize: 13, lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{addOk.message}</div>
          {addOk.magic_link && (
            <>
              <div style={{
                background: theme.bg, border: `1px solid ${theme.border}`,
                padding: 10, marginTop: 10, marginBottom: 8,
                fontSize: 11, fontFamily: theme.font.mono,
                wordBreak: 'break-all', color: theme.text,
              }}>
                {addOk.magic_link}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => {
                  navigator.clipboard.writeText(addOk.magic_link!);
                  alert('Magic link copiado al portapapeles');
                }} style={{
                  background: theme.brand, color: theme.textOnBrand, border: 'none',
                  padding: '8px 14px', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
                  fontFamily: theme.font.sans,
                }}>
                  📋 Copiar link
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `Hola ${addOk.email?.split('@')[0]}, ya tienes acceso al admin de NULED OS. Abre este link para entrar:\n\n${addOk.magic_link}\n\nEs de un solo uso, vence en 1 hora.`
                  )}`}
                  target="_blank" rel="noreferrer"
                  style={{
                    background: '#25D366', color: '#FFFFFF',
                    padding: '8px 14px', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    textDecoration: 'none', fontFamily: theme.font.sans,
                    display: 'inline-block',
                  }}
                >
                  💬 Enviar por WhatsApp
                </a>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: theme.textMuted }}>
                Este link vence en 1 hora y es de un solo uso. Si vence, vuelve a esta pantalla y genera otro con el botón "Regenerar link" (próximamente).
              </div>
            </>
          )}
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: theme.textMuted }}>Cargando…</div>
      ) : (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bgSoft }}>
                <Th>Nombre</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Estado</Th>
                <Th align="right">Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {list.map(row => {
                const isMe = row.user_id === user?.id;
                return (
                  <tr key={row.user_id} style={{ borderTop: `1px solid ${theme.border}` }}>
                    <Td>
                      <div style={{ fontWeight: 700, color: theme.brand }}>
                        {row.display_name} {isMe && <span style={{ fontSize: 10, color: theme.accentDark, marginLeft: 6 }}>(tú)</span>}
                      </div>
                    </Td>
                    <Td><code style={{ fontSize: 12, color: theme.textMuted }}>{row.email ?? '—'}</code></Td>
                    <Td>
                      <select
                        value={row.role}
                        disabled={isMe}
                        onChange={e => changeRole(row, e.target.value as StaffRow['role'])}
                        style={{ ...selectStyle, padding: '6px 28px 6px 10px', fontSize: 12, width: 'auto' }}
                      >
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </Td>
                    <Td>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        background: row.active ? theme.accentSoft : '#FEE2E2',
                        color: row.active ? theme.brand : theme.err,
                        fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        {row.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </Td>
                    <Td align="right">
                      <button
                        onClick={() => toggleActive(row)}
                        disabled={isMe && row.active}
                        title={isMe && row.active ? 'No puedes desactivarte' : undefined}
                        style={btnSecondary}
                      >
                        {row.active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        onClick={() => removeStaff(row)}
                        disabled={isMe}
                        title={isMe ? 'No puedes eliminarte' : undefined}
                        style={{ ...btnSecondary, marginLeft: 6, color: theme.err, borderColor: theme.err }}
                      >
                        Eliminar
                      </button>
                    </Td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><Td colSpan={5}>
                  <div style={{ textAlign: 'center', color: theme.textMuted, padding: 24 }}>
                    No hay usuarios staff. Agrega el primero arriba.
                  </div>
                </Td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pie informativo */}
      <div style={{
        marginTop: 24, padding: 16, background: theme.bgSoft, border: `1px solid ${theme.border}`,
        fontSize: 12, color: theme.textMuted, lineHeight: 1.6,
      }}>
        <strong style={{ color: theme.brand }}>Notas:</strong>
        <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
          <li>Solo usuarios con role <code>admin</code> ven esta pantalla.</li>
          <li>Al agregar un usuario nuevo, Supabase manda un email con magic link al correo capturado.</li>
          <li>Desactivar le quita acceso inmediato al admin sin borrar su cuenta.</li>
          <li>Eliminar borra solo la fila staff — la cuenta auth.users sigue existiendo (puedes convertirlo en customer después si quieres).</li>
        </ul>
      </div>
    </AdminContainer>
  );
}

// ============= helpers UI =============

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: 11, color: theme.textMuted, marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
      }}>{label}</div>
      {children}
    </label>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th style={{
      padding: '12px 14px',
      textAlign: align ?? 'left',
      fontSize: 11, fontWeight: 700,
      color: theme.textMuted,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{children}</th>
  );
}

function Td({ children, align, colSpan }: { children: React.ReactNode; align?: 'left' | 'right' | 'center'; colSpan?: number }) {
  return (
    <td style={{ padding: '12px 14px', textAlign: align ?? 'left' }} colSpan={colSpan}>{children}</td>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      color: theme.err, fontSize: 12, marginBottom: 12,
      padding: 10, background: 'rgba(220,38,38,0.05)',
      border: `1px solid ${theme.err}`,
    }}>{children}</div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '10px 12px', color: theme.text, fontSize: 13,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1 L6 6 L11 1' stroke='%232D484F' stroke-width='1.5' fill='none'/></svg>")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: 32,
  cursor: 'pointer',
};

const btnPrimary: React.CSSProperties = {
  background: theme.accent, color: theme.brand, border: 'none',
  padding: '10px 18px', fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
  fontFamily: theme.font.sans,
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: theme.brand,
  border: `1px solid ${theme.borderStrong}`,
  padding: '6px 12px', fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
  fontFamily: theme.font.sans,
};
